import { Field } from '@/components/ui/field';
import { InputGroup } from '@/components/ui/input-group';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { currencySymbol } from '@/pages/collabs/brief-templates/_shared/format-money';
import { SimpleGrid, Text, VStack, createListCollection } from '@chakra-ui/react';
import {
  DISPLAY_RIGHTS_MAX_DURATION_MONTHS,
  DISPLAY_RIGHTS_MIN_DURATION_MONTHS,
} from '@fluenceur/types';
import { useMemo } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DISPLAY_RIGHTS_MIN_AMOUNT,
  type DisplayRightsComposerValues,
} from './display-rights-composer-schema';

interface DisplayRightsTermsFieldsProps {
  control: Control<DisplayRightsComposerValues>;
  /** Resolved server-side; the composer only ever displays it. */
  currencyCode: string;
}

/**
 * The money half of the composer: a monthly amount and a run time in months.
 *
 * V1 offers the monthly model only, so the "/ month" suffix is a label, not a
 * choice — a flat fee stays a server capability with no UI. The indicative range
 * is deliberately static copy: a per-media estimate would be a number the
 * product cannot yet stand behind.
 */
export function DisplayRightsTermsFields({ control, currencyCode }: DisplayRightsTermsFieldsProps) {
  const { t } = useTranslation();

  // Same collection shape as the boost setup's own duration picker, so both
  // surfaces of the product pick a run time through one control.
  const durationCollection = useMemo(
    () =>
      createListCollection({
        items: Array.from(
          { length: DISPLAY_RIGHTS_MAX_DURATION_MONTHS - DISPLAY_RIGHTS_MIN_DURATION_MONTHS + 1 },
          (_, index) => {
            const months = DISPLAY_RIGHTS_MIN_DURATION_MONTHS + index;
            return {
              value: String(months),
              label: t('displayRights.composer.durationOption', { count: months }),
            };
          }
        ),
      }),
    [t]
  );

  return (
    <VStack align="stretch" gap="2">
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="3">
        <Controller
          control={control}
          name="amount"
          render={({ field, fieldState }) => (
            <Field
              label={t('displayRights.composer.amountLabel')}
              invalid={!!fieldState.error}
              errorText={fieldState.error ? t(fieldState.error.message ?? '') : undefined}
            >
              {/* No spinner stack: it collided with the "/ month" suffix on the
                  same edge, and stepping a compensation amount by clicks is not
                  a gesture anyone wants. The group reads left to right —
                  currency, the figure typed, the period. */}
              <NumberInputRoot
                min={DISPLAY_RIGHTS_MIN_AMOUNT}
                step={5}
                value={field.value}
                onValueChange={({ value }) => field.onChange(value)}
                colorPalette="gray"
                size="sm"
                w="full"
              >
                <InputGroup
                  startElement={
                    <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                      {currencySymbol(currencyCode)}
                    </Text>
                  }
                  startOffset="4px"
                  endElement={
                    <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                      {t('displayRights.composer.amountSuffix')}
                    </Text>
                  }
                  endOffset="6px"
                  w="full"
                >
                  <NumberInputField
                    fontWeight="medium"
                    onBlur={field.onBlur}
                    data-testid="display-rights-amount"
                  />
                </InputGroup>
              </NumberInputRoot>
            </Field>
          )}
        />

        <Controller
          control={control}
          name="durationMonths"
          render={({ field, fieldState }) => (
            <Field
              label={t('displayRights.composer.durationLabel')}
              invalid={!!fieldState.error}
              errorText={fieldState.error ? t(fieldState.error.message ?? '') : undefined}
            >
              <SelectRoot
                collection={durationCollection}
                size="sm"
                value={[String(field.value)]}
                onValueChange={(details) => field.onChange(Number(details.value[0]))}
                onInteractOutside={field.onBlur}
              >
                <SelectTrigger data-testid="display-rights-duration">
                  <SelectValueText />
                </SelectTrigger>
                <SelectContent>
                  {durationCollection.items.map((item) => (
                    <SelectItem key={item.value} item={item}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </Field>
          )}
        />
      </SimpleGrid>

      {/* Under the grid, not under the amount: as a field helper it pushed the
          amount cell taller than the duration cell and the two baselines
          drifted apart. It is guidance on the pair, so it sits under the pair. */}
      <Text fontSize="xs" color="fg.muted" data-testid="display-rights-range-hint">
        {t('displayRights.composer.rangeHint', { currency: currencySymbol(currencyCode) })}
      </Text>
    </VStack>
  );
}
